import { useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, For, Match, Show, Switch, type JSX } from "solid-js";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { ComingSoonToast, comingSoon } from "../components/ComingSoon";
import { ReportPartSheet } from "../components/ReportSheets";
import { TextField } from "../components/TextField";
import {
  PARTS,
  canSend,
  isComingSoon,
  kindOf,
  parseReportParams,
  partChips,
  partsSummary,
  recapLines,
  setReported,
  stepAfter,
  stepBefore,
  type PartId,
  type Reported,
  type ReportStep,
} from "../report";

/**
 * Figma flow 9 "Report a problem after a ride" (R2–R6), at `/signaler?vehicle=&trip=&back=`.
 * R1 is `ReportChoiceSheet`, mounted by the screen it opens over. Client-side only: « Envoyer »
 * sends nothing yet (no Pony report API), it shows the thank-you screen.
 */
export function Report() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const params = () => parseReportParams(search);
  const [step, setStep] = createSignal<ReportStep>("intro");
  const [showAll, setShowAll] = createSignal(false);
  const [reported, setReportedList] = createSignal<Reported[]>([]);
  const [detail, setDetail] = createSignal<PartId>();
  const [text, setText] = createSignal("");
  let main!: HTMLElement;

  const go = (s: ReportStep | "exit") => {
    // A focused field removed by the step change would leave iOS's page shifted (see viewport.ts).
    (document.activeElement as HTMLElement | null)?.blur?.();
    if (s === "exit") return navigate(params().back, { replace: true });
    setStep(s);
    main.scrollTo({ top: 0 });
  };
  const bikeLabel = () => (params().vehicle ? `Vélo ${params().vehicle}` : "Vélo");

  return (
    <main ref={(el) => (main = el)} class="screen bg-surface">
      <AppBar title="Signalement" close={step() !== "parts"} back={() => go(stepBefore(step()))} />
      <Switch>
        <Match when={step() === "intro"}>
          {/* R2 Report intro (116:14571) */}
          <div class="screen-body px-[15px] pt-[22px] pb-[max(50px,env(safe-area-inset-bottom))] short:pt-2">
            <img
              src="/img/report/r2-mechanic.webp"
              width="264"
              height="259"
              alt=""
              class="mx-auto h-[259px] w-[264px] short:h-[180px] short:w-auto"
            />
            <Show when={params().vehicle}>
              <span class="mx-auto mt-[11px] inline-flex h-7 items-center self-center rounded-[14px] bg-surface2 px-4 text-[13px] font-medium leading-[1.25] tracking-[0.13px] text-violet">
                {bikeLabel()}
              </span>
            </Show>
            <h1 class="mt-[18px] px-[15px] text-center text-2xl font-medium leading-[1.25] tracking-[0.24px] text-violet">
              Vous allez signaler un problème sur le vélo
            </h1>
            <p class="mt-2 px-[21px] text-center text-sm leading-[1.25] tracking-[0.14px] text-text">
              Ce que vous décrivez ici est transmis à nos mécaniciens. Plus votre signalement est
              précis, plus on répare vite et plus le vélo est vite de retour dans les rues.
            </p>
            <div class="mt-auto flex shrink-0 flex-col gap-[10px] pt-6">
              <Button onClick={() => go(stepAfter("intro"))}>Continuer</Button>
              <Button variant="outline" onClick={() => isComingSoon("intro-other") && comingSoon()}>
                J'ai un autre problème
              </Button>
            </div>
          </div>
        </Match>
        <Match when={step() === "parts"}>
          {/* R3 / R3b part grid (116:14713, 116:14768), R5 part reported (116:14876) */}
          <div class="screen-body px-[15px] pt-[18px] pb-[max(50px,env(safe-area-inset-bottom))]">
            <h1 class="px-[5px] text-[22px] font-medium leading-[1.25] tracking-[0.22px] text-violet">
              Qu'est-ce qui n'allait pas ?
            </h1>
            <p class="mt-1 px-[5px] text-sm leading-[1.25] tracking-[0.14px] text-text">
              Choisissez la partie concernée
            </p>
            <div class="mt-[14.5px] grid grid-cols-2 gap-x-3 gap-y-[10px]">
              <For each={partChips(showAll())}>
                {(c) =>
                  c.kind === "more" ? (
                    <Chip emoji={c.emoji} label={c.label} onClick={() => setShowAll(true)} />
                  ) : (
                    <Chip
                      emoji={c.part.emoji}
                      label={c.part.label}
                      selected={kindOf(reported(), c.part.id) !== undefined}
                      onClick={() => setDetail(c.part.id)}
                    />
                  )
                }
              </For>
            </div>
            <TextField
              id="report-text"
              class="mt-6"
              label="Dites-nous en plus :"
              placeholder="Plus c'est précis, plus on répare vite"
              autocomplete="off"
              maxLength={500}
              value={text()}
              onInput={(e) => setText(e.currentTarget.value)}
            />
            <div class="mt-auto shrink-0 pt-6">
              <Show when={canSend(reported())}>
                <ul
                  class={`flex flex-col gap-2 ${showAll() ? "mb-6" : "mb-[94px] short:mb-6"}`}
                  aria-label="Parties signalées"
                >
                  <For each={recapLines(reported())}>
                    {(l) => (
                      <li class="flex h-10 items-center gap-[9px] rounded-[10px] bg-surface2 px-3 text-sm leading-[1.25] tracking-[0.14px]">
                        <img src="/icons/check-circle-green-20.svg" width="20" height="20" alt="" />
                        <span class="truncate">
                          <span class="font-medium text-violet">{l.part}</span>
                          <span class="text-text">{l.text}</span>
                        </span>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
              <Button disabled={!canSend(reported())} onClick={() => go(stepAfter("parts"))}>
                Envoyer
              </Button>
            </div>
          </div>
          <Show when={detail()} keyed>
            {(id) => (
              <ReportPartSheet
                part={PARTS.find((p) => p.id === id)!}
                kind={kindOf(reported(), id)}
                onClose={() => setDetail(undefined)}
                onConfirm={(k) => {
                  setReportedList((list) => setReported(list, id, k));
                  setDetail(undefined);
                }}
              />
            )}
          </Show>
        </Match>
        <Match when={step() === "thanks"}>
          {/* R6 Thank you (116:14938) */}
          <div class="screen-body px-[15px] pt-4 pb-[max(50px,env(safe-area-inset-bottom))]">
            <img src="/icons/check-circle-56.svg" width="56" height="56" alt="" class="mx-auto" />
            <h1 class="mt-2 text-center text-[28px] font-medium leading-[1.25] tracking-[0.28px] text-violet">
              Merci
            </h1>
            <p class="mt-[5px] px-[25px] text-center text-sm leading-[1.25] tracking-[0.14px] text-text">
              Grâce à votre vigilance, le prochain rider aura un vélo en bon état.
            </p>
            <div class="mt-[17px] flex min-h-16 items-center gap-[11px] rounded-[14px] bg-surface2 py-[10px] pl-4 pr-4">
              <img src="/icons/bike-indigo-26.svg" width="26" height="26" alt="" class="shrink-0" />
              <div class="min-w-0 self-start">
                <div class="text-[15px] font-semibold leading-[1.25] tracking-[0.15px] text-strong">
                  {bikeLabel()}
                </div>
                <div class="mt-[3px] text-xs leading-[1.25] tracking-[0.12px] text-text">
                  {partsSummary(reported())}
                </div>
              </div>
            </div>
            <ol class="mt-8 flex flex-col px-[5px] short:mt-5">
              <Step
                disc={
                  <span class="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-green">
                    <img src="/icons/check-white-20.svg" width="20" height="20" alt="" />
                  </span>
                }
                title="C'est entre de bonnes mains"
                body="Votre signalement est arrivé chez nos mécaniciens."
              />
              <Step
                disc={<NumberDisc n={2} />}
                title="Un mécanicien vérifie et répare"
                body="Votre description l'oriente vers la bonne partie à vérifier, puis il répare le vélo."
              />
              <Step
                disc={<NumberDisc n={3} />}
                title="Le vélo repart dans vos rues"
                body="Réparation faite et contrôle en profondeur effectué, le vélo repart pour vous servir."
              />
            </ol>
            <div class="mt-auto shrink-0 pt-6">
              <Button onClick={() => go("exit")}>Terminer</Button>
            </div>
          </div>
        </Match>
      </Switch>
      <ComingSoonToast />
    </main>
  );
}

/** Figma R3 part chip: 46 px, white, 1 px #e0e0e0, radius 10; emoji 16 at 14 px, label at 44 px.
 *  Selected (inferred, not drawn): violet border on lilac, like the R4 pill. */
function Chip(props: { emoji: string; label: string; selected?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      class={`flex h-[46px] min-w-0 items-center rounded-[10px] border pl-[13px] pr-2 text-left ${props.selected ? "border-violet bg-surface2" : "border-border bg-white active:bg-surface2"}`}
      aria-pressed={props.selected === undefined ? undefined : props.selected}
      onClick={() => props.onClick()}
    >
      <span class="w-[30px] shrink-0 text-base leading-[1.25]" aria-hidden="true">
        {props.emoji}
      </span>
      <span class="truncate text-sm font-medium leading-[1.25] tracking-[0.14px] text-strong">
        {props.label}
      </span>
    </button>
  );
}

/** Figma R6 step: 34 px disc, title SemiBold 15 at 48 px, body Regular 13; 86 px pitch. */
function Step(props: { disc: JSX.Element; title: string; body: string }) {
  return (
    <li class="flex min-h-[86px] gap-[14px] last:min-h-0 short:min-h-[66px]">
      <span class="shrink-0">{props.disc}</span>
      <div class="pt-[2px]">
        <div class="text-[15px] font-semibold leading-[1.25] tracking-[0.15px] text-strong">
          {props.title}
        </div>
        <p class="mt-[3px] text-[13px] leading-[1.25] tracking-[0.13px] text-text">{props.body}</p>
      </div>
    </li>
  );
}

function NumberDisc(props: { n: number }) {
  return (
    <span class="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-surface2 text-base font-semibold leading-[1.25] tracking-[0.16px] text-violet">
      {props.n}
    </span>
  );
}
